import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import { comUrlAbsoluta } from "../../utils/suporte/urlAnexo";
import { Comentario, ComposerPayload, Demanda } from "./types";

const TTL_DETALHE_MS = 5 * 60 * 1000;
const PREFIXO_CACHE = "suporte:detalhe:";

export interface UsuarioAtual {
  id: string;
  nome: string;
  foto?: string;
}

export interface FalhaSuporte {
  mensagem: string;
  codigo: string;
}

function mensagemDoErro(error: unknown, padrao: string): FalhaSuporte {
  const resposta = (error as { response?: { data?: unknown } })?.response
    ?.data as { error?: string; message?: string; code?: string } | undefined;

  return {
    mensagem: resposta?.error || resposta?.message || padrao,
    codigo: resposta?.code || "ERRO_DESCONHECIDO",
  };
}

function lerUsuarioAtual(): UsuarioAtual {
  let id = "";
  let foto: string | undefined;
  let nome = "";

  try {
    const sessao = JSON.parse(localStorage.getItem("1") || "{}");
    id = String(sessao?.refreshToken?.usuarioClienteId ?? "");
  } catch {
    id = "";
  }

  try {
    const bruto = localStorage.getItem("perfil");
    if (bruto) {
      const perfil = JSON.parse(bruto);
      const base =
        perfil?.nome || perfil?.nomeFantasia || perfil?.razaoSocial || "";
      const sobrenome = perfil?.sobrenome ? ` ${perfil.sobrenome}` : "";
      nome = `${base}${base ? sobrenome : ""}`.trim();
      foto = perfil?.foto || undefined;
    }
  } catch {
    nome = "";
  }

  return { id, nome: nome || "Você", foto };
}

function lerCacheDetalhe(id: string): Demanda | null {
  try {
    const bruto = sessionStorage.getItem(`${PREFIXO_CACHE}${id}`);
    if (!bruto) return null;
    const guardado = JSON.parse(bruto);
    if (!guardado?.timestamp) return null;
    if (Date.now() - guardado.timestamp > TTL_DETALHE_MS) return null;
    return guardado.data as Demanda;
  } catch {
    return null;
  }
}

function gravarCacheDetalhe(id: string, data: Demanda) {
  try {
    sessionStorage.setItem(
      `${PREFIXO_CACHE}${id}`,
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    /* sessionStorage indisponível (aba anônima, cota cheia) */
  }
}

function limparCacheDetalhe(id: string) {
  try {
    sessionStorage.removeItem(`${PREFIXO_CACHE}${id}`);
  } catch {
    /* nada a fazer */
  }
}

function hidratarAnexos(comentario: Comentario): Comentario {
  return {
    ...comentario,
    anexos: comUrlAbsoluta(comentario.anexos ?? []),
    respostas: (comentario.respostas ?? []).map(hidratarAnexos),
  };
}

function hidratarDemanda(demanda: Demanda): Demanda {
  return {
    ...demanda,
    abertura: demanda.abertura
      ? {
          ...demanda.abertura,
          anexos: comUrlAbsoluta(demanda.abertura.anexos ?? []),
        }
      : null,
    comentarios: (demanda.comentarios ?? []).map(hidratarAnexos),
  };
}

export function useDemandas() {
  const [demandas, setDemandas] = useState<Demanda[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<FalhaSuporte | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [online, setOnline] = useState(true);
  const [modo, setModo] = useState<string | null>(null);
  const [sincronizadoEm, setSincronizadoEm] = useState<string | null>(null);
  const usuario = useMemo(lerUsuarioAtual, []);
  const carregandoDetalhe = useRef(new Set<string>());

  const somenteLeitura = modo !== null && modo !== "LOCAL";

  const carregarLista = useCallback(async (comLoading = true) => {
    if (comLoading) setLoading(true);
    try {
      const { data } = await api.get("/suporte/demandas");
      setDemandas(data.demandas ?? []);
      setOnline(Boolean(data.online));
      setModo(data.modo ?? null);
      setSincronizadoEm(data.sincronizadoEm ?? null);
      setAviso(data.aviso ?? null);
      setErro(null);
    } catch (error) {
      setErro(
        mensagemDoErro(
          error,
          "Não foi possível carregar os chamados.",
        ),
      );
    } finally {
      if (comLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  const aplicarDetalhe = useCallback((detalhe: Demanda) => {
    setDemandas((atuais) =>
      atuais.map((demanda) =>
        demanda.id === detalhe.id
          ? { ...demanda, ...detalhe, erroSync: null }
          : demanda,
      ),
    );
  }, []);

  const carregarDetalhe = useCallback(
    async (id: string) => {
      if (!id || somenteLeitura) return;
      if (carregandoDetalhe.current.has(id)) return;

      const doCache = lerCacheDetalhe(id);
      if (doCache) {
        aplicarDetalhe(doCache);
        return;
      }

      carregandoDetalhe.current.add(id);
      try {
        const { data } = await api.post("/suporte/demandas/detalhes", { id });
        const detalhe = hidratarDemanda(data.demanda);
        gravarCacheDetalhe(id, detalhe);
        aplicarDetalhe(detalhe);
      } catch (error) {
        const falha = mensagemDoErro(
          error,
          "Não foi possível carregar a conversa.",
        );
        setDemandas((atuais) =>
          atuais.map((demanda) =>
            demanda.id === id
              ? { ...demanda, detalheCarregado: true, erroSync: falha.mensagem }
              : demanda,
          ),
        );
        throw falha;
      } finally {
        carregandoDetalhe.current.delete(id);
      }
    },
    [aplicarDetalhe, somenteLeitura],
  );

  const invalidarDetalhe = useCallback(
    async (demandaId: string) => {
      limparCacheDetalhe(demandaId);
      const { data } = await api.post("/suporte/demandas/detalhes", {
        id: demandaId,
      });
      const detalhe = hidratarDemanda(data.demanda);
      gravarCacheDetalhe(demandaId, detalhe);
      aplicarDetalhe(detalhe);
    },
    [aplicarDetalhe],
  );

  const logsLoginClienteId = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem("logs") || "{}")?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const criarDemanda = useCallback(
    async (titulo: string, payload: ComposerPayload) => {
      try {
        const { data } = await api.post("/suporte/demandas/criar", {
          titulo,
          descricao: payload.texto,
          imagens: payload.imagens,
          logsLoginClienteId: logsLoginClienteId(),
        });

        const demanda = hidratarDemanda(data.demanda);
        setDemandas((atuais) => [demanda, ...atuais]);
        if (data.pendenteSync) setOnline(false);

        return { demanda, aviso: data.aviso ?? null };
      } catch (error) {
        throw mensagemDoErro(
          error,
          "Não foi possível abrir o chamado.",
        );
      }
    },
    [logsLoginClienteId],
  );

  const adicionarComentario = useCallback(
    async (demandaId: string, payload: ComposerPayload) => {
      try {
        await api.post("/suporte/demandas/comentario", {
          id: demandaId,
          texto: payload.texto,
          imagens: payload.imagens,
          logsLoginClienteId: logsLoginClienteId(),
        });
        await invalidarDetalhe(demandaId);
      } catch (error) {
        throw mensagemDoErro(
          error,
          "Não foi possível enviar o comentário.",
        );
      }
    },
    [invalidarDetalhe, logsLoginClienteId],
  );

  const adicionarResposta = useCallback(
    async (
      demandaId: string,
      comentarioId: string,
      payload: ComposerPayload,
    ) => {
      try {
        await api.post(
          `/suporte/demandas/comentario/${encodeURIComponent(comentarioId)}/resposta`,
          {
            texto: payload.texto,
            imagens: payload.imagens,
            logsLoginClienteId: logsLoginClienteId(),
          },
        );
        await invalidarDetalhe(demandaId);
      } catch (error) {
        throw mensagemDoErro(
          error,
          "Não foi possível enviar a resposta.",
        );
      }
    },
    [invalidarDetalhe, logsLoginClienteId],
  );

  const editarComentario = useCallback(
    async (
      demandaId: string,
      comentarioId: string,
      payload: ComposerPayload,
    ) => {
      try {
        await api.patch("/suporte/demandas/comentario", {
          id: comentarioId,
          texto: payload.texto,
          novasImagens: payload.imagens,
          anexosMantidos: payload.anexosMantidos,
          logsLoginClienteId: logsLoginClienteId(),
        });
        await invalidarDetalhe(demandaId);
      } catch (error) {
        throw mensagemDoErro(
          error,
          "Não foi possível salvar a edição.",
        );
      }
    },
    [invalidarDetalhe, logsLoginClienteId],
  );

  const arquivarDemanda = useCallback(
    async (demandaId: string, arquivado: boolean) => {
      try {
        const { data } = await api.patch("/suporte/demandas/arquivar", {
          id: demandaId,
          arquivado,
          logsLoginClienteId: logsLoginClienteId(),
        });

        const arquivada = Boolean(data?.demanda?.arquivada ?? arquivado);
        setDemandas((atuais) =>
          atuais.map((demanda) =>
            demanda.id === demandaId ? { ...demanda, arquivada } : demanda,
          ),
        );

        return arquivada;
      } catch (error) {
        throw mensagemDoErro(
          error,
          arquivado
            ? "Não foi possível arquivar o chamado."
            : "Não foi possível restaurar o chamado.",
        );
      }
    },
    [logsLoginClienteId],
  );

  const ressincronizar = useCallback(async () => {
    try {
      await api.post("/suporte/demandas/sincronizar", {});
      await carregarLista(false);
      return null;
    } catch (error) {
      const falha = mensagemDoErro(
        error,
        "Não foi possível sincronizar agora.",
      );
      await carregarLista(false);
      return falha;
    }
  }, [carregarLista]);

  return {
    demandas,
    loading,
    erro,
    aviso,
    online,
    modo,
    somenteLeitura,
    sincronizadoEm,
    usuario,
    carregarDetalhe,
    criarDemanda,
    adicionarComentario,
    adicionarResposta,
    editarComentario,
    arquivarDemanda,
    ressincronizar,
  };
}
